var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ApiProperty, ApiPropertyOptional, IntersectionType, PartialType, PickType } from "@nestjs/swagger";
import { Type } from "class-transformer";
import { IsBoolean, IsDate, IsEmail, IsEnum, IsNumber, IsOptional, IsString, ValidateIf, ValidateNested } from "class-validator";
import { ConsentDataResourceScope, ConsentDataScope, ConsentDataSensitivityScope } from "./consentscopes.dtos";
import { GlobalPatientReference } from "./patient.dtos";
import { ScreenshotMetadataStatus } from "./screenshot.dtos";
import { StudyWithIncludesDto } from "./study-with-includes.dtos";
import { BaseStudySearchInput } from "./study.dtos";
import { USAStates } from "./usaStates";
import { willNeverHappen } from "./utils";
export var IdentifiedPatientLastActivityType;
(function (IdentifiedPatientLastActivityType) {
    IdentifiedPatientLastActivityType["PRESCREENING_REQUEST_SENT"] = "PRESCREENING_REQUEST_SENT";
    IdentifiedPatientLastActivityType["ENROLLMENT_REQUEST_SENT"] = "ENROLLMENT_REQUEST_SENT";
    IdentifiedPatientLastActivityType["REMINDER_PRESCREENING_REQUEST_SENT"] = "REMINDER_PRESCREENING_REQUEST_SENT";
    IdentifiedPatientLastActivityType["REMINDER_ENROLLMENT_REQUEST_SENT"] = "REMINDER_ENROLLMENT_REQUEST_SENT";
    IdentifiedPatientLastActivityType["REENGAED_PRESCREENING_REQUEST_SENT"] = "REENGAED_PRESCREENING_REQUEST_SENT";
    IdentifiedPatientLastActivityType["REENGAED_ENROLLMENT_REQUEST_SENT"] = "REENGAED_ENROLLMENT_REQUEST_SENT";
    IdentifiedPatientLastActivityType["CONSENTED_TO_PRESCREENING"] = "CONSENTED_TO_PRESCREENING";
    IdentifiedPatientLastActivityType["CONSENTED_TO_ENROLLMENT"] = "CONSENTED_TO_ENROLLMENT";
    IdentifiedPatientLastActivityType["DECLINED_PRESCREENING"] = "DECLINED_PRESCREENING";
    IdentifiedPatientLastActivityType["DECLINED_ENROLLMENT"] = "DECLINED_ENROLLMENT";
    IdentifiedPatientLastActivityType["REVOKED_PRESCREENING"] = "REVOKED_PRESCREENING";
    IdentifiedPatientLastActivityType["REVOKED_ENROLLMENT"] = "REVOKED_ENROLLMENT";
    IdentifiedPatientLastActivityType["CONTACTED_BY_RESEARCHER"] = "CONTACTED_BY_RESEARCHER";
    IdentifiedPatientLastActivityType["HEALTH_RECORD_UPDATED"] = "HEALTH_RECORD_UPDATED";
    IdentifiedPatientLastActivityType["HEALTH_RECORD_UPDATE_REQUEST_SENT"] = "HEALTH_RECORD_UPDATE_REQUEST_SENT";
})(IdentifiedPatientLastActivityType || (IdentifiedPatientLastActivityType = {}));
export var PatientDataRefreshStatus;
(function (PatientDataRefreshStatus) {
    PatientDataRefreshStatus["SUCCESS"] = "SUCCESS";
    PatientDataRefreshStatus["FAILED"] = "FAILED";
    PatientDataRefreshStatus["IN_PROGRESS"] = "IN_PROGRESS";
})(PatientDataRefreshStatus || (PatientDataRefreshStatus = {}));
export var IdentifiedPatientState;
(function (IdentifiedPatientState) {
    IdentifiedPatientState["INFORMATION_SENT"] = "INFORMATION_SENT";
    IdentifiedPatientState["PRESCREEN_CONSENT_PENDING"] = "PRESCREEN_CONSENT_PENDING";
    IdentifiedPatientState["PRESCREEN_CONSENTED"] = "PRESCREEN_CONSENTED";
    IdentifiedPatientState["ENROLLING"] = "ENROLLING";
    IdentifiedPatientState["ENROLLING_INTERESTED"] = "ENROLLING_INTERESTED";
    IdentifiedPatientState["ENROLLING_CONTACTED"] = "ENROLLING_CONTACTED";
    IdentifiedPatientState["ENROLLED"] = "ENROLLED";
    IdentifiedPatientState["DECLINED_PRESCREEN"] = "DECLINED_PRESCREEN";
    IdentifiedPatientState["DECLINED_ENROLLMENT"] = "DECLINED_ENROLLMENT";
    IdentifiedPatientState["REVOKED_PRESCREEN"] = "REVOKED_PRESCREEN";
    IdentifiedPatientState["REVOKED_ENROLLMENT"] = "REVOKED_ENROLLMENT";
    IdentifiedPatientState["REMOVED"] = "REMOVED";
})(IdentifiedPatientState || (IdentifiedPatientState = {}));
export var ConsentType;
(function (ConsentType) {
    ConsentType["DATA_AUTHORIZATION"] = "DATA_AUTHORIZATION";
    ConsentType["PATIENT_DIRECTED_DATA_EXCHANGE"] = "PATIENT_DIRECTED_DATA_EXCHANGE";
    ConsentType["FUTURE_DATA_USE"] = "FUTURE_DATA_USE";
    ConsentType["ACCESS_CONSENT"] = "ACCESS_CONSENT";
})(ConsentType || (ConsentType = {}));
export const PublicConsentType = {
    DATA_AUTHORIZATION: ConsentType.DATA_AUTHORIZATION,
    PATIENT_DIRECTED_DATA_EXCHANGE: ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE,
    FUTURE_DATA_USE: ConsentType.FUTURE_DATA_USE,
};
export var FutureDataUseConsentType;
(function (FutureDataUseConsentType) {
    FutureDataUseConsentType["ALLOW_IDENTIFIED"] = "ALLOW_IDENTIFIED";
    FutureDataUseConsentType["ALLOW_DEIDENTIFIED"] = "ALLOW_DEIDENTIFIED";
    FutureDataUseConsentType["NOT_ALLOWED"] = "NOT_ALLOWED";
})(FutureDataUseConsentType || (FutureDataUseConsentType = {}));
export const dataConsents = [ConsentType.DATA_AUTHORIZATION, ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE, ConsentType.ACCESS_CONSENT];
export function isDataConsent(consentType) {
    return dataConsents.includes(consentType);
}
export var ConsentUIFlow;
(function (ConsentUIFlow) {
    ConsentUIFlow["PATIENT_DIRECTED_DATA_EXCHANGE"] = "PATIENT_DIRECTED_DATA_EXCHANGE";
})(ConsentUIFlow || (ConsentUIFlow = {}));
const validConsentsForUIFlow = {
    [ConsentType.DATA_AUTHORIZATION]: [],
    [ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE]: [ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE],
    [ConsentType.FUTURE_DATA_USE]: [],
    [ConsentType.ACCESS_CONSENT]: [ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE],
};
export function isConsentValidForUIFlow(consentType, uiFlow) {
    return validConsentsForUIFlow[consentType].includes(uiFlow);
}
export function isConsentValidForScreenshot(consentType, uiFlow) {
    return ((consentType === ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE || consentType === ConsentType.ACCESS_CONSENT) &&
        uiFlow === ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE);
}
const validConsentDataSensitivityScopesForConsent = {
    DATA_AUTHORIZATION: [
        ConsentDataSensitivityScope.NORMAL,
        ConsentDataSensitivityScope.HIV_OR_STI,
        ConsentDataSensitivityScope.SUBSTANCE_USE_OR_DRUG_ABUSE,
        ConsentDataSensitivityScope.MENTAL_HEALTH,
        ConsentDataSensitivityScope.GENETIC_TESTING,
        ConsentDataSensitivityScope.REPRODUCTIVE_HEALTH_DATA,
        ConsentDataSensitivityScope.SICKLE_CELL,
    ],
    PATIENT_DIRECTED_DATA_EXCHANGE: "FULL_PATIENT_RECORD",
    ACCESS_CONSENT: "FULL_PATIENT_RECORD",
};
export function isConsentDataSensitivityScopeValidForConsent(consentType, scope) {
    var _a, _b;
    return (_b = (_a = validConsentDataSensitivityScopesForConsent[consentType]) === null || _a === void 0 ? void 0 : _a.includes(scope)) !== null && _b !== void 0 ? _b : false;
}
export var InteractionType;
(function (InteractionType) {
    InteractionType["USED_DEFAULTS"] = "USED_DEFAULTS";
    InteractionType["READ_FAQS"] = "READ_FAQS";
    InteractionType["READ_ADDITIONAL_RESOURCES"] = "READ_ADDITIONAL_RESOURCES";
    InteractionType["ASKED_QUESTIONS"] = "ASKED_QUESTIONS";
})(InteractionType || (InteractionType = {}));
export class ConsentSessionStats {
}
__decorate([
    IsNumber()
], ConsentSessionStats.prototype, "sessionLength", void 0);
__decorate([
    IsEnum(InteractionType, { each: true })
], ConsentSessionStats.prototype, "interactions", void 0);
export var ConsentStatus;
(function (ConsentStatus) {
    ConsentStatus["OPTED_IN"] = "OPTED_IN";
    ConsentStatus["OPTED_OUT"] = "OPTED_OUT";
})(ConsentStatus || (ConsentStatus = {}));
export var ConsentReason;
(function (ConsentReason) {
    ConsentReason["CONSENTED"] = "CONSENTED";
    ConsentReason["DECLINED"] = "DECLINED";
    ConsentReason["REVOKED"] = "REVOKED";
    ConsentReason["EXPIRED"] = "EXPIRED";
    ConsentReason["NO_RESPONSE"] = "NO_RESPONSE";
})(ConsentReason || (ConsentReason = {}));
export var ActivityTimelineEntryType;
(function (ActivityTimelineEntryType) {
    ActivityTimelineEntryType["AGREED"] = "AGREED";
    ActivityTimelineEntryType["DECLINED"] = "DECLINED";
    ActivityTimelineEntryType["ENDED_SHARING"] = "ENDED_SHARING";
    ActivityTimelineEntryType["RECORDS_SHARED"] = "RECORDS_SHARED";
    ActivityTimelineEntryType["RECORDS_UPDATED"] = "RECORDS_UPDATED";
})(ActivityTimelineEntryType || (ActivityTimelineEntryType = {}));
export class ActivityTimelineEntry {
}
__decorate([
    IsEnum(ActivityTimelineEntryType)
], ActivityTimelineEntry.prototype, "type", void 0);
__decorate([
    Type(() => Date),
    IsDate()
], ActivityTimelineEntry.prototype, "timestamp", void 0);
class BaseConsentUpdateCommon {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsOptional(),
    IsDate(),
    Type(() => Date)
], BaseConsentUpdateCommon.prototype, "expirationDate", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "patientName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "signerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "signingReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "revocationReason", void 0);
__decorate([
    IsString({ each: true }),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "pagesViewed", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "mp4Url", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "posterUrl", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "consentedByLink", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "providerId", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "providerSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "providerSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "parentGuardianSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "parentGuardianSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "additionalParentGuardianSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "additionalParentGuardianSigningReason", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "additionalParentGuardianExemptSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "minorParticipantSignerName", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "minorParticipantExemptFromAssent", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "patientMRN", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "larSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "larSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "larParticipantSignerName", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "larParticipantExemptSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "larParticipantSigningReason", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "witnessSkipSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "witnessSignerName", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "isProxyAccess", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], BaseConsentUpdateCommon.prototype, "loggedInDuringConsent", void 0);
export class ConsentUpdate extends PartialType(BaseConsentUpdateCommon) {
    constructor(fields) {
        super(fields);
        Object.assign(this, fields);
    }
}
__decorate([
    IsEnum(ConsentStatus)
], ConsentUpdate.prototype, "consentStatus", void 0);
__decorate([
    IsEnum(USAStates),
    IsOptional()
], ConsentUpdate.prototype, "patientState", void 0);
__decorate([
    Type(() => ConsentSessionStats),
    ValidateNested(),
    IsOptional()
], ConsentUpdate.prototype, "consentStats", void 0);
__decorate([
    IsEnum(ConsentUIFlow),
    ValidateIf((_, value) => value !== null)
], ConsentUpdate.prototype, "consentUIFlow", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentUpdate.prototype, "uiHash", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentUpdate.prototype, "language", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentUpdate.prototype, "fullRecordWithoutRestrictedData", void 0);
__decorate([
    IsEnum(ConsentDataResourceScope, { each: true }),
    IsOptional()
], ConsentUpdate.prototype, "consentDataResourceScopes", void 0);
__decorate([
    IsEnum(ConsentDataSensitivityScope, { each: true }),
    IsOptional()
], ConsentUpdate.prototype, "consentDataSensitivityScopes", void 0);
export class ConsentUpdateBatch {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsEnum(ConsentType, { each: true })
], ConsentUpdateBatch.prototype, "consentTypes", void 0);
__decorate([
    Type(() => ConsentUpdate),
    ValidateNested()
], ConsentUpdateBatch.prototype, "update", void 0);
export class ConsentUpdateFromUI extends IntersectionType(PickType(ConsentUpdate, [
    "consentUIFlow",
    "uiHash",
    "consentDataResourceScopes",
    "consentDataSensitivityScopes",
    "fullRecordWithoutRestrictedData",
]), BaseConsentUpdateCommon) {
    constructor(fields) {
        super(fields);
        Object.assign(this, fields);
    }
}
export class ConsentUpdateUIInput extends PartialType(ConsentUpdateFromUI) {
}
export class RevokeAllConsentsInput {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsEnum(USAStates),
    IsOptional()
], RevokeAllConsentsInput.prototype, "patientState", void 0);
export class ConsentDto {
}
__decorate([
    IsString()
], ConsentDto.prototype, "patientId", void 0);
__decorate([
    IsEnum(ConsentType)
], ConsentDto.prototype, "consentType", void 0);
__decorate([
    IsEnum(ConsentStatus)
], ConsentDto.prototype, "consentStatus", void 0);
__decorate([
    IsString()
], ConsentDto.prototype, "consentRecordId", void 0);
__decorate([
    IsEnum(ScreenshotMetadataStatus),
    IsOptional()
], ConsentDto.prototype, "proofOfConsentPdfStatus", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentDto.prototype, "proofOfConsentPdfUrl", void 0);
__decorate([
    Type(() => Date),
    IsDate()
], ConsentDto.prototype, "timestamp", void 0);
__decorate([
    Type(() => Date),
    IsDate()
], ConsentDto.prototype, "expiration", void 0);
__decorate([
    IsEnum(ConsentDataResourceScope, { each: true }),
    IsOptional()
], ConsentDto.prototype, "consentDataResourceScopes", void 0);
__decorate([
    IsEnum(ConsentDataSensitivityScope, { each: true }),
    IsOptional()
], ConsentDto.prototype, "consentDataSensitivityScopes", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentDto.prototype, "consentedByLink", void 0);
__decorate([
    IsDate(),
    IsOptional()
], ConsentDto.prototype, "interestedUpdated", void 0);
export class ConsentsDto {
}
__decorate([
    Type(() => StudyWithIncludesDto),
    ValidateNested()
], ConsentsDto.prototype, "study", void 0);
__decorate([
    Type(() => ConsentDto),
    ValidateNested({ each: true })
], ConsentsDto.prototype, "consents", void 0);
export class ConsentFlowProgressDto {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsString(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "studyId", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "studyVersionId", void 0);
__decorate([
    IsEmail(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "email", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "consentedByLink", void 0);
__decorate([
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "consentedByLinkUpdatedAt", void 0);
__decorate([
    Type(() => Date),
    IsDate()
], ConsentFlowProgressDto.prototype, "startedAt", void 0);
__decorate([
    Type(() => Date),
    IsDate()
], ConsentFlowProgressDto.prototype, "lastUpdatedAt", void 0);
__decorate([
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "resumedAt", void 0);
__decorate([
    IsNumber()
], ConsentFlowProgressDto.prototype, "nudgeCount", void 0);
__decorate([
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "lastNudgeSentAt", void 0);
__decorate([
    Type(() => Date),
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "clearLoginAttemptedAt", void 0);
__decorate([
    Type(() => Date),
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "accountSetupShownAt", void 0);
__decorate([
    Type(() => Date),
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "consentShownAt", void 0);
__decorate([
    Type(() => Date),
    IsDate(),
    IsOptional()
], ConsentFlowProgressDto.prototype, "retrievalStartedAt", void 0);
export class ConsentFlowProgressUpdate {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsString(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "studyId", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "studyVersionId", void 0);
__decorate([
    IsEmail(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "email", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "consentedByLink", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "clearLoginAttempted", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "accountSetupShown", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "consentShown", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentFlowProgressUpdate.prototype, "retrievalStarted", void 0);
export class ConsentFlowProgressInput {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsString(),
    IsOptional()
], ConsentFlowProgressInput.prototype, "studyId", void 0);
export class ConsentSearchIncludes {
}
__decorate([
    IsOptional()
], ConsentSearchIncludes.prototype, "proofOfConsentPdf", void 0);
export class ConsentDateSearchInput {
}
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "eq", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "lt", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "lte", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "gt", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "gte", void 0);
__decorate([
    IsDate(),
    Type(() => Date),
    IsOptional()
], ConsentDateSearchInput.prototype, "ne", void 0);
export class StudyConsentSearchInput {
}
__decorate([
    ValidateNested(),
    Type(() => BaseStudySearchInput),
    IsOptional()
], StudyConsentSearchInput.prototype, "study", void 0);
__decorate([
    IsString(),
    IsOptional()
], StudyConsentSearchInput.prototype, "studyId", void 0);
__decorate([
    IsEnum(ConsentType, { each: true }),
    IsOptional()
], StudyConsentSearchInput.prototype, "consentType", void 0);
__decorate([
    IsEnum(ConsentStatus, { each: true }),
    IsOptional()
], StudyConsentSearchInput.prototype, "consentStatus", void 0);
__decorate([
    IsString(),
    IsOptional()
], StudyConsentSearchInput.prototype, "patientId", void 0);
__decorate([
    IsEnum(USAStates),
    IsOptional()
], StudyConsentSearchInput.prototype, "patientState", void 0);
__decorate([
    ValidateNested(),
    Type(() => ConsentDateSearchInput),
    IsOptional()
], StudyConsentSearchInput.prototype, "timestamp", void 0);
__decorate([
    ValidateNested(),
    Type(() => ConsentDateSearchInput),
    IsOptional()
], StudyConsentSearchInput.prototype, "expirationTimestamp", void 0);
__decorate([
    ApiPropertyOptional(),
    Type(() => ConsentSearchIncludes),
    ValidateNested(),
    IsOptional()
], StudyConsentSearchInput.prototype, "include", void 0);
export class ConsentSearchResult {
}
__decorate([
    IsNumber()
], ConsentSearchResult.prototype, "total", void 0);
__decorate([
    Type(() => ConsentsDto),
    ValidateNested({ each: true })
], ConsentSearchResult.prototype, "results", void 0);
export class RequestConsentInput {
    constructor() {
        this.isResend = false;
    }
}
__decorate([
    IsString({ each: true })
], RequestConsentInput.prototype, "identifiedPatientIds", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], RequestConsentInput.prototype, "isResend", void 0);
__decorate([
    IsEnum(ConsentType),
    IsOptional()
], RequestConsentInput.prototype, "primaryConsentType", void 0);
export var RequestConsentError;
(function (RequestConsentError) {
    RequestConsentError["ALREADY_OPTED_IN"] = "ALREADY_OPTED_IN";
    RequestConsentError["INTERNAL_ERROR"] = "INTERNAL_ERROR";
})(RequestConsentError || (RequestConsentError = {}));
export var ConsentFlowProgressError;
(function (ConsentFlowProgressError) {
    ConsentFlowProgressError["STUDY_ID_REQUIRED"] = "STUDY_ID_REQUIRED";
})(ConsentFlowProgressError || (ConsentFlowProgressError = {}));
export class PatientWithError {
}
__decorate([
    IsString()
], PatientWithError.prototype, "identifiedPatientId", void 0);
__decorate([
    IsEnum(RequestConsentError)
], PatientWithError.prototype, "error", void 0);
export class RequestConsentResult {
}
__decorate([
    Type(() => GlobalPatientReference),
    ValidateNested({ each: true })
], RequestConsentResult.prototype, "successIdentifiedPatientIds", void 0);
__decorate([
    Type(() => PatientWithError),
    ValidateNested({ each: true })
], RequestConsentResult.prototype, "errorPatients", void 0);
export class PatientConsentSearchInput {
    constructor() {
        this.excludeAnyPatientsWithConsents = false;
    }
}
__decorate([
    ApiProperty({
        enum: PublicConsentType,
        enumName: "PublicConsentType",
        isArray: true,
        description: "The type of consent to search for. See documentation for more details on each type.",
    }),
    IsEnum(PublicConsentType, { each: true })
], PatientConsentSearchInput.prototype, "consentTypes", void 0);
__decorate([
    ApiPropertyOptional({
        type: "boolean",
        isArray: true,
        description: "Include to change whether to search for only for patients who have consented (the default), patients who have not consented, or both.",
    }),
    IsBoolean({ each: true }),
    IsOptional()
], PatientConsentSearchInput.prototype, "hasConsented", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for only a single patient's consents. This must be a valid HealthEx patient reference id.\n\n" +
            "Only one patient identifier may be included.",
    })
], PatientConsentSearchInput.prototype, "patientId", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for only a single patient's consents. This must be a valid patient Medical Record Number.\n\n" +
            "Only one patient identifier may be included.",
    })
], PatientConsentSearchInput.prototype, "patientMRN", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for only a single patient's consents. This must be a valid patient email address.\n\n" +
            "Only one patient identifier may be included.",
    })
], PatientConsentSearchInput.prototype, "email", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for only a single patient's consents. This must be a valid patient phone number.\n\n" +
            "Only one patient identifier may be included.",
    })
], PatientConsentSearchInput.prototype, "phone", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for only a single patient's consents. This must be a valid patient external identifier.\n\n" +
            "Only one patient identifier may be included.",
    })
], PatientConsentSearchInput.prototype, "externalId", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search only for consents for a specific study.",
    })
], PatientConsentSearchInput.prototype, "studyId", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to search for expired consents.",
    })
], PatientConsentSearchInput.prototype, "expiredConsents", void 0);
__decorate([
    IsNumber(),
    IsOptional()
], PatientConsentSearchInput.prototype, "start", void 0);
__decorate([
    IsNumber(),
    IsOptional()
], PatientConsentSearchInput.prototype, "end", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], PatientConsentSearchInput.prototype, "excludeAnyPatientsWithConsents", void 0);
__decorate([
    IsOptional(),
    ApiPropertyOptional({
        description: "Include to compute and return the reason for each consent decision.",
    }),
    IsBoolean()
], PatientConsentSearchInput.prototype, "includeReason", void 0);
export var ConsentSource;
(function (ConsentSource) {
    ConsentSource["STUDY"] = "STUDY";
    ConsentSource["DEFAULTS"] = "DEFAULTS";
    ConsentSource["IAS"] = "IAS";
})(ConsentSource || (ConsentSource = {}));
export class ConsentRecord {
}
__decorate([
    ApiProperty(),
    IsString()
], ConsentRecord.prototype, "id", void 0);
__decorate([
    ApiProperty(),
    IsString()
], ConsentRecord.prototype, "patientId", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "studyId", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "studyVersionId", void 0);
__decorate([
    ApiProperty({ enum: PublicConsentType, enumName: "PublicConsentType" }),
    IsEnum(ConsentType)
], ConsentRecord.prototype, "consentType", void 0);
__decorate([
    ApiProperty({ enum: ConsentStatus, enumName: "ConsentStatus" }),
    IsEnum(ConsentStatus)
], ConsentRecord.prototype, "consentStatus", void 0);
__decorate([
    ApiProperty(),
    IsDate(),
    Type(() => Date)
], ConsentRecord.prototype, "timestamp", void 0);
__decorate([
    ApiProperty(),
    IsDate(),
    Type(() => Date)
], ConsentRecord.prototype, "expirationTimestamp", void 0);
__decorate([
    ApiPropertyOptional(),
    IsOptional(),
    Type(() => ConsentSessionStats),
    ValidateNested()
], ConsentRecord.prototype, "consentStats", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "studySnapshotId", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "consentConfigSnapshotId", void 0);
__decorate([
    ApiProperty({ enum: ConsentUIFlow, enumName: "ConsentUIFlow" }),
    IsEnum(ConsentUIFlow),
    IsOptional()
], ConsentRecord.prototype, "consentUIFlow", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "uiHash", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "language", void 0);
__decorate([
    ApiPropertyOptional(),
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "fullRecordWithoutRestrictedData", void 0);
__decorate([
    ApiProperty({ enum: ConsentDataResourceScope, enumName: "ConsentDataResourceScope", isArray: true }),
    IsEnum(ConsentDataResourceScope, { each: true }),
    IsOptional()
], ConsentRecord.prototype, "consentDataResourceScopes", void 0);
__decorate([
    ApiProperty({ enum: ConsentDataSensitivityScope, enumName: "ConsentDataSensitivityScope", isArray: true }),
    IsEnum(ConsentDataSensitivityScope, { each: true }),
    IsOptional()
], ConsentRecord.prototype, "consentDataSensitivityScopes", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "providerId", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "patientName", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "signerName", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "signingReason", void 0);
__decorate([
    ApiProperty({
        isArray: true,
        type: String,
    }),
    IsString({ each: true }),
    IsOptional()
], ConsentRecord.prototype, "pagesViewed", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "mp4Url", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "posterUrl", void 0);
__decorate([
    ApiProperty(),
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "consentedByLink", void 0);
__decorate([
    ApiProperty(),
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "providerSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "providerSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "parentGuardianSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "parentGuardianSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "additionalParentGuardianSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "additionalParentGuardianSigningReason", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "additionalParentGuardianExemptSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "minorParticipantSignerName", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "minorParticipantExemptFromAssent", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "patientMRN", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "larSignerName", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "larSigningReason", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "larParticipantSignerName", void 0);
__decorate([
    IsOptional()
], ConsentRecord.prototype, "larParticipantExemptSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "larParticipantSigningReason", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "witnessSkipSignature", void 0);
__decorate([
    IsString(),
    IsOptional()
], ConsentRecord.prototype, "witnessSignerName", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "isProxyAccess", void 0);
__decorate([
    IsBoolean(),
    IsOptional()
], ConsentRecord.prototype, "loggedInDuringConsent", void 0);
export class BaseEvaluatedConsent {
}
__decorate([
    ApiProperty({ description: "The type of consent that was recorded", enum: PublicConsentType, enumName: "PublicConsentType" }),
    IsEnum(ConsentType)
], BaseEvaluatedConsent.prototype, "consentType", void 0);
__decorate([
    ApiProperty({ description: "Whether the patient consented or not" }),
    IsBoolean()
], BaseEvaluatedConsent.prototype, "hasPatientConsented", void 0);
__decorate([
    ApiProperty({
        description: "What kinds of data the patient has consented to disclose. Null if the patient has not consented.",
        isArray: true,
        type: ConsentDataScope,
    }),
    Type(() => ConsentDataScope),
    ValidateNested({ each: true }),
    IsOptional()
], BaseEvaluatedConsent.prototype, "consentDataScopes", void 0);
__decorate([
    ApiPropertyOptional({
        description: "The ID of the consent record that recorded this consent. This ID can be used in subsequent API calls to get even more detail",
    }),
    IsString(),
    IsOptional()
], BaseEvaluatedConsent.prototype, "consentRecordId", void 0);
__decorate([
    ApiPropertyOptional({
        description: "When this consent record expires, if applicable.",
    }),
    IsDate(),
    IsOptional(),
    Type(() => Date)
], BaseEvaluatedConsent.prototype, "expirationTimestamp", void 0);
__decorate([
    ApiPropertyOptional({
        description: "The URL of the PDF of the consent record. Deprecated: proof-of-consent PDFs are being phased out. This field is still populated today but new integrations should not depend on it.",
        deprecated: true,
    }),
    IsString(),
    IsOptional()
], BaseEvaluatedConsent.prototype, "pdfUrl", void 0);
__decorate([
    ApiPropertyOptional({
        description: "The reason for the consent decision. Only present if `includeReason` is true.",
        enum: ConsentReason,
        enumName: "ConsentReason",
    }),
    IsEnum(ConsentReason),
    IsOptional()
], BaseEvaluatedConsent.prototype, "reason", void 0);
__decorate([
    ApiPropertyOptional({
        description: "The consent record object for this consent.",
        type: ConsentRecord,
    }),
    Type(() => ConsentRecord),
    IsOptional()
], BaseEvaluatedConsent.prototype, "consentRecord", void 0);
export class EvaluatedConsent extends BaseEvaluatedConsent {
}
__decorate([
    ApiProperty({ description: "The study that the consent was recorded for, or null in the case of an IAS consent" }),
    IsString(),
    IsOptional()
], EvaluatedConsent.prototype, "studyId", void 0);
__decorate([
    ApiProperty({
        enum: ConsentSource,
        enumName: "ConsentSource",
        description: "If a patient explicitly gave or declined consent for a study, this will be `STUDY`. If a patient did not give a consent response at all, this will be `DEFAULTS`.\n\n `IAS` indicates a consent that was given for the patient's own use, and will never be returned by this API.",
    }),
    IsEnum(ConsentSource)
], EvaluatedConsent.prototype, "source", void 0);
export class EvaluatedBroadConsent extends BaseEvaluatedConsent {
}
export class EvaluatedConsentDataScopes {
}
__decorate([
    ApiProperty({
        description: "What kinds of data the patient has consented to disclose.",
        isArray: true,
        type: ConsentDataScope,
    }),
    Type(() => ConsentDataScope),
    ValidateNested({ each: true }),
    IsOptional()
], EvaluatedConsentDataScopes.prototype, "consentDataScopes", void 0);
__decorate([
    ApiProperty({
        description: "The IDs of the consent records that recorded the relevant consents. These IDs can be used in subsequent API calls to get even more detail",
        isArray: true,
        type: String,
    }),
    IsString({ each: true })
], EvaluatedConsentDataScopes.prototype, "consentRecordIds", void 0);
__decorate([
    ApiProperty({
        description: "Indicates that the patient has consented to disclose their entire medical record. In this case, `consentDataScopes` can be ignored.",
        type: Boolean,
    }),
    IsBoolean()
], EvaluatedConsentDataScopes.prototype, "fullRecordWithoutRestrictedData", void 0);
__decorate([
    ApiPropertyOptional({
        description: "List of URLs of the PDF of consent records. Deprecated: proof-of-consent PDFs are being phased out. This field is still populated today but new integrations should not depend on it.",
        isArray: true,
        deprecated: true,
    }),
    IsString({ each: true }),
    IsOptional()
], EvaluatedConsentDataScopes.prototype, "pdfUrls", void 0);
export class PatientConsents {
}
__decorate([
    ApiProperty({ description: "The patient that is giving consent" }),
    IsString()
], PatientConsents.prototype, "patientId", void 0);
__decorate([
    ApiProperty({
        isArray: true,
        type: EvaluatedConsent,
        description: "A list of all the consents (matching the search criteria) that this patient has given",
    }),
    Type(() => EvaluatedConsent),
    ValidateNested({ each: true })
], PatientConsents.prototype, "consents", void 0);
export function getConsentUIFlowFromConsentType(consentType) {
    switch (consentType) {
        case ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE:
        case ConsentType.ACCESS_CONSENT:
            return ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE;
        case ConsentType.DATA_AUTHORIZATION:
        case ConsentType.FUTURE_DATA_USE:
            return null;
    }
    return willNeverHappen(consentType);
}
export class PatientConsentStatusResponseDto {
}
__decorate([
    IsOptional(),
    IsString()
], PatientConsentStatusResponseDto.prototype, "projectId", void 0);
__decorate([
    IsOptional(),
    IsString()
], PatientConsentStatusResponseDto.prototype, "consentRecordId", void 0);
__decorate([
    IsOptional(),
    IsString()
], PatientConsentStatusResponseDto.prototype, "expiresAt", void 0);
__decorate([
    IsBoolean()
], PatientConsentStatusResponseDto.prototype, "hasPatientConsented", void 0);
export class ConsentStateSearchInput {
}
__decorate([
    IsEnum(ConsentType)
], ConsentStateSearchInput.prototype, "consentType", void 0);
__decorate([
    IsEnum(ConsentStatus)
], ConsentStateSearchInput.prototype, "consentStatus", void 0);
export const primaryConsentTypes = [ConsentType.DATA_AUTHORIZATION, ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE, ConsentType.ACCESS_CONSENT];
export function doesProjectAllowIdentifiedFutureDataUse(project) {
    return !!project.futureUseOfDataEnabled && project.futureUseOfData === FutureDataUseConsentType.ALLOW_IDENTIFIED;
}
export function getDataConsentTypeForProject(project) {
    return project.pdda ? ConsentType.PATIENT_DIRECTED_DATA_EXCHANGE : ConsentType.DATA_AUTHORIZATION;
}
export function getConsentTypesForProject(project, futureUseOfDataEnabled) {
    const consentTypes = [];
    consentTypes.push(getDataConsentTypeForProject(project));
    if (futureUseOfDataEnabled)
        consentTypes.push(ConsentType.FUTURE_DATA_USE);
    return consentTypes;
}
export function getPrimaryConsentTypeForProject(project) {
    const primaryConsentType = getConsentTypesForProject(project).find((consentType) => primaryConsentTypes.includes(consentType));
    if (primaryConsentType)
        return primaryConsentType;
    throw new Error(`No primary consent type found for project. projectType: ${project.projectType}, pdda: ${project.pdda}`);
}
export function getIASConsentTypes() {
    const consentTypes = [];
    consentTypes.push(ConsentType.ACCESS_CONSENT);
    return consentTypes;
}
export function getPrimaryIASConsentType() {
    const primaryIASConsentType = getIASConsentTypes().find((consentType) => primaryConsentTypes.includes(consentType));
    if (primaryIASConsentType)
        return primaryIASConsentType;
    throw new Error(`No primary IAS consent type found`);
}
